import { FormEvent, useState } from 'react';
import cn from 'classnames';
import { useMutation } from '@tanstack/react-query';
import { apiSubmitUserReview } from 'api';
import { track } from '../../lib';
import { Button } from '../button';
// import { StarPicker } from './star-picker';
import { SimpleRating } from './simple-rating';
import styles from './user-review-form.module.scss';

const NAME_MIN = 2;
const NAME_MAX = 255;
const TEXT_MIN = 50;
const TEXT_MAX = 10000;

type UserReviewFormProps = {
  bookId: number;
};

const detectLanguage = (text: string) => {
  const cyrillic = text.match(/[\u0400-\u04FF]/g)?.length ?? 0;
  const latin = text.match(/[a-z]/gi)?.length ?? 0;

  return cyrillic > latin ? 'ru' : 'en';
};

const errorMessage = (error: unknown) => {
  const status = (error as { status?: number } | null)?.status;

  if (status === 429) {
    return 'You’ve sent a few reviews in a row. Take a breath and try again in a minute.';
  }

  if (status === 422) {
    return 'Something in the form doesn’t look right. Please check it and try again.';
  }

  return 'We couldn’t send your review. Please try again.';
};

export const UserReviewForm = ({ bookId }: UserReviewFormProps) => {
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [website, setWebsite] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const name = author.trim();
  const body = text.trim();
  const errors = {
    rating: rating === null ? 'Pick a rating from 1 to 10.' : '',
    author:
      name.length < NAME_MIN
        ? `Your name needs at least ${NAME_MIN} characters.`
        : '',
    text:
      body.length < TEXT_MIN
        ? `A review needs at least ${TEXT_MIN} characters.`
        : '',
  };
  const isValid = !errors.rating && !errors.author && !errors.text;

  const mutation = useMutation({
    mutationFn: () => {
      if (website) {
        return Promise.resolve();
      }

      return apiSubmitUserReview({
        author: name,
        text: body,
        rating: rating ?? 0,
        book_id: bookId,
        language: detectLanguage(body),
      });
    },
    onSuccess: () => {
      track('submit_user_review', { bookId, rating });
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);

    if (isValid && !mutation.isPending) {
      mutation.mutate();
    }
  };

  if (mutation.isSuccess) {
    return (
      <div className={cn(styles.card, styles.done)} role="status">
        <span className={styles.doneMark} aria-hidden="true">
          ✓
        </span>
        <div>
          <h3 className={styles.title}>Thank you, {name}!</h3>
          <p className={styles.note}>
            Your review is with us now. One of us reads every review before it
            goes live so it can take a little while.
          </p>
        </div>
      </div>
    );
  }

  const isLongEnough = body.length >= TEXT_MIN;

  return (
    <form className={styles.card} onSubmit={handleSubmit} noValidate>
      <div className={styles.intro}>
        <h3 className={styles.title}>Read it? Tell us what you think</h3>
        <p className={styles.note}>
          No account needed. One of us reads every review before it goes live.
        </p>
      </div>

      <fieldset className={styles.field}>
        <legend className={styles.label}>Your rating</legend>
        {/* <StarPicker
          value={rating}
          onChange={setRating}
          invalid={showErrors && !!errors.rating}
        /> */}
        <SimpleRating
          value={rating}
          onChange={setRating}
          invalid={showErrors && !!errors.rating}
        />
        {showErrors && errors.rating && (
          <span className={styles.error}>{errors.rating}</span>
        )}
      </fieldset>

      <label className={styles.field}>
        <span className={styles.label}>Your name</span>
        <input
          className={cn(styles.input, {
            [styles.invalid]: showErrors && errors.author,
          })}
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          maxLength={NAME_MAX}
          autoComplete="nickname"
          placeholder="How should we sign it?"
        />
        {showErrors && errors.author && (
          <span className={styles.error}>{errors.author}</span>
        )}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Your review</span>
        <textarea
          className={cn(styles.input, styles.textarea, {
            [styles.invalid]: showErrors && errors.text,
          })}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={TEXT_MAX}
          rows={6}
          placeholder="What stayed with you? Who would you give it to?"
        />
        <span
          className={cn(styles.counter, {
            [styles.counterDone]: isLongEnough,
            [styles.error]: showErrors && errors.text,
          })}
        >
          {isLongEnough
            ? `✓ ${body.length} characters`
            : `Minimum ${TEXT_MIN} characters · ${body.length} so far`}
        </span>
      </label>

      <input
        className={styles.trap}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />

      <div className={styles.footer}>
        {mutation.isError && (
          <span className={styles.error} role="alert">
            {errorMessage(mutation.error)}
          </span>
        )}
        <Button
          type="submit"
          className={styles.submit}
          loading={mutation.isPending}
        >
          Send review
        </Button>
      </div>
    </form>
  );
};
